import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { fixToTwoDecimal, formatNumbersToCurrencyString } from 'utils/utils';
import { billableExpenseBody } from './helpers/expensesDataTableHelpers';
import TextSnippetWrapper from 'components/TextSnippetWrapper';

const Expenses = (props) => {
  const {
    isContigencyFee,
    expenses: initialExpenses,
    setExpenses,
    expenseTypes,
    expenseError,
    setExpenseError,
    isChanged,
    setIsChanged,
    addExpense,
    teamMembers,
    deleteExpense,
    expenseTotal,
    caseDetails,
    createExpenses,
    updateExpenses,
    setAllExpensesSaved,
    createExpenseInvoiceClicked,
    setExpenseTotal,
  } = props || {};

  const [modifiedRows, setModifiedRows] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const disableExpense = isContigencyFee && !caseDetails?.billing_preferences?.is_expense;

  const markRowAsModified = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev, [rowIndex]: true };
      setAllExpensesSaved(false);
      return updated;
    });

    setIsChanged((prev) => ({ ...prev, time_entries: true }));
  };

  const markRowAsUnmodified = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];

      const hasOtherChanges = Object.keys(updated).length > 0;

      setAllExpensesSaved(!hasOtherChanges);
      setIsChanged((prev) => ({ ...prev, time_entries: hasOtherChanges }));

      return updated;
    });
  };
  const handleRowDeletion = (rowIndex) => {
    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      setAllExpensesSaved(Object.keys(updated).length === 0);
      return updated;
    });

    setEditingRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setIsChanged((prev) => ({
      ...prev,
      time_entries: Object.keys(modifiedRows).filter((key) => key !== rowIndex.toString()).length > 0,
    }));
  };
  const handleSaveRow = async (rowIndex) => {
    const rowData = editingRows[rowIndex] || initialExpenses[rowIndex];
    const errors = {};
    const feeType = rowData?.fee_type;

    if (!rowData.expense_date) {
      errors[`${rowIndex}_date`] = 'Required';
    }
    if (!rowData.team_member_id) {
      errors[`${rowIndex}_team_member`] = 'Required';
    }
    if (!rowData.expense_type_id) {
      errors[`${rowIndex}_expense_type`] = 'Required';
    }

    if (feeType === 'hourly') {
      if (!rowData.rate_per_unit || parseFloat(rowData.rate_per_unit) <= 0) {
        errors[`${rowIndex}_rate_per_unit`] = 'Required';
      }
      if (!rowData.quantity || parseFloat(rowData.quantity) <= 0) {
        errors[`${rowIndex}_quantity`] = 'Required';
      }
      // Always compute total on save for Hourly
      const total = (Number(rowData.rate_per_unit) || 0) * (Number(rowData.quantity) || 0);
      rowData.total_amount = fixToTwoDecimal(total);
      // clear any fixed-only error
      if (expenseError?.[`${rowIndex}_total_amount`]) {
        errors[`${rowIndex}_total_amount`] = '';
      }
    } else {
      // Fixed
      const total = parseFloat(rowData.total_amount);
      if (!total || total <= 0) {
        errors[`${rowIndex}_total_amount`] = 'Required';
      }

      rowData.rate_per_unit = '';
      rowData.quantity = '';
      // clear hourly-only errors if they exist
      if (expenseError?.[`${rowIndex}_rate_per_unit`]) errors[`${rowIndex}_rate_per_unit`] = '';
      if (expenseError?.[`${rowIndex}_quantity`]) errors[`${rowIndex}_quantity`] = '';
    }

    if (Object.keys(errors).length > 0) {
      setExpenseError((prev) => ({ ...prev, ...errors }));
      return;
    }

    const onExpenseSuccess = () => {
      delete rowData.new_expense_added;

      const updatedExpenses = [...initialExpenses];
      updatedExpenses[rowIndex] = rowData;
      setExpenses(updatedExpenses);

      const updatedEditingRows = { ...editingRows };
      delete updatedEditingRows[rowIndex];
      setEditingRows(updatedEditingRows);

      markRowAsUnmodified(rowIndex);
    };

    console.log('rowData', rowData);

    try {
      if (rowData.new_expense_added && !rowData?.pk) {
        await createExpenses(rowData, onExpenseSuccess);
      } else {
        await updateExpenses(rowData, onExpenseSuccess);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getRowData = (rowIndex) => {
    return editingRows[rowIndex] || initialExpenses[rowIndex];
  };

  const onChangeBillable = async (e, index) => {
    const checked = e?.target?.checked;
    const updatedRows = { ...editingRows };
    const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

    rowData.billable = checked;
    updatedRows[index] = rowData;
    setEditingRows(updatedRows);

    const updatedTimeEntries = [...initialExpenses];
    updatedTimeEntries[index] = rowData;
    setExpenses(updatedTimeEntries); // Triggers total recalculation

    // 3. Check if ONLY billable was changed (no other edits)
    const isOnlyBillableChange = !modifiedRows[index] && Object.keys(editingRows[index] || {}).length <= 1;

    isChanged.time_entries = true;
    setIsChanged({ ...isChanged });

    if (isOnlyBillableChange) {
      try {
        delete rowData.added_new_time_entry;

        const updatedEditingRows = { ...editingRows };
        delete updatedEditingRows[index];
        setEditingRows(updatedEditingRows);

        markRowAsUnmodified(index);
        setAllExpensesSaved(true);
      } catch (err) {
        console.error(err);

        rowData.billable = !checked;
        updatedRows[index] = rowData;
        setEditingRows(updatedRows);
        updatedTimeEntries[index] = rowData;
        setExpenses(updatedTimeEntries);
      }
    } else {
      markRowAsModified(index);
    }
  };

  useEffect(() => {
    const combinedTimeEntries = initialExpenses.map((entry, idx) => {
      return editingRows[idx] ? editingRows[idx] : entry;
    });

    let subTotal = 0;

    combinedTimeEntries.forEach((entry) => {
      if (entry.billable) {
        let total_amount = entry.total_amount;
        total_amount = total_amount ? parseFloat(total_amount) : 0;
        subTotal += total_amount;
        subTotal = +parseFloat(subTotal).toFixed(2);
      }
    });

    setExpenseTotal(fixToTwoDecimal(subTotal));
  }, [initialExpenses, editingRows]);

  // Add Fee Type options
  const feeTypeOptions = [
    { label: 'Hourly', value: 'hourly' },
    { label: 'Fixed', value: 'fixed' },
  ];

  const getFeeType = (row) => row?.fee_type || 'hourly';

  return (
    <div className="mt-4">
      <div className="caption-bold mb-2">Expenses</div>
      <div className="card-bg">
        <div className="row">
          <div className="col-12 ">
            <div className="datatable-responsive">
              <DataTable
                className="p-datatable-responsive"
                value={initialExpenses}
                emptyMessage={() => <div onClick={addExpense}>No records found</div>}
              >
                <Column
                  header="Date"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Date</span>
                        <span className="text-break">
                          {rowEntry.expense_date ? format(new Date(rowEntry?.expense_date), 'MM/dd/yyyy') : <i>Please Enter</i>}
                        </span>
                        <br></br>
                        {expenseError && expenseError[`${props.rowIndex}_date`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_date`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Date</span>
                        <div className="table-input">
                          <Calendar
                            value={rowData?.expense_date ? new Date(rowData.expense_date) : null}
                            onChange={(e) => {
                              let index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              rowData.expense_date = format(e.value, 'MM/dd/yyyy');
                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);

                              expenseError[`${props.rowIndex}_date`] = '';
                              setExpenseError({ ...expenseError });
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                              markRowAsModified(index);
                            }}
                          />
                          {expenseError && expenseError[`${props.rowIndex}_date`] && (
                            <span className="text-danger">{expenseError[`${props.rowIndex}_date`]}</span>
                          )}
                        </div>
                      </>
                    );
                  }}
                />
                <Column
                  header="Members"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Members</span>
                        <span className="text-break">{rowEntry.team_member_name ? rowEntry.team_member_name : <i>Please Enter</i>}</span>
                        <br></br>
                        {expenseError && expenseError[`${props.rowIndex}_team_member`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_team_member`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Members</span>
                        <Dropdown
                          options={teamMembers}
                          optionLabel="name"
                          value={{
                            name: rowData?.team_member_name,
                            contact_id: rowData?.team_member_id,
                          }}
                          placeholder="Select"
                          onChange={(e) => {
                            let index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            rowData.team_member_name = e.target.value?.name;
                            rowData.team_member_id = e.target.value?.contact_id;
                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);

                            expenseError[`${props.rowIndex}_team_member`] = '';
                            setExpenseError({ ...expenseError });
                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                        />
                        {expenseError && expenseError[`${props.rowIndex}_team_member`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_team_member`]}</span>
                        )}
                      </>
                    );
                  }}
                />
                <Column
                  header="Expense Type"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Expense Type</span>
                        <span className="text-break">{rowEntry.expense_type ? rowEntry.expense_type : <i>Please Enter</i>}</span>
                        <br></br>
                        {expenseError && expenseError[`${props.rowIndex}_expense_type`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_expense_type`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Expense Type</span>
                        <Dropdown
                          value={{
                            expense_type: rowData?.expense_type,
                            expense_type_id: rowData?.expense_type_id,
                          }}
                          options={expenseTypes}
                          onChange={(e) => {
                            let index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            rowData.expense_type = e.target?.value?.expense_type;
                            rowData.expense_type_id = e.target?.value?.expense_type_id;
                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);

                            expenseError[`${props.rowIndex}_expense_type`] = '';
                            setExpenseError({ ...expenseError });
                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                          optionLabel="expense_type"
                          filter
                          placeholder="Select"
                        />
                        {expenseError && expenseError[`${props.rowIndex}_expense_type`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_expense_type`]}</span>
                        )}
                      </>
                    );
                  }}
                />
                <Column
                  header="Description"
                  field="description"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Description</span>
                        <span className="text-break">{rowEntry.description ? rowEntry.description : <i>Please Enter</i>}</span>
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Description</span>
                        <TextSnippetWrapper
                          type="text"
                          value={rowData?.description}
                          onChange={(e) => {
                            let index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            rowData.description = e.target.value;
                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);

                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                          className="w-100 input-shadow"
                          autoComplete="off"
                        />
                      </>
                    );
                  }}
                />
                <Column
                  header="Fee Type"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Fee Type</span>
                        <span className="text-break text-capitalize">{rowEntry.fee_type || 'Hourly'}</span>
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Fee Type</span>
                        <Dropdown
                          value={rowData?.fee_type || 'hourly'}
                          options={feeTypeOptions}
                          onChange={(e) => {
                            const index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            rowData.fee_type = e.value;

                            // Reset fields depending on Fee Type
                            if (e.value === 'fixed') {
                              rowData.rate_per_unit = null;
                              rowData.quantity = null;
                              rowData.total_amount = rowData.total_amount || 0;
                              // clear hourly errors
                              if (expenseError?.[`${index}_rate_per_unit`]) expenseError[`${index}_rate_per_unit`] = '';
                              if (expenseError?.[`${index}_quantity`]) expenseError[`${index}_quantity`] = '';
                            } else {
                              rowData.rate_per_unit = rowData.rate_per_unit || '';
                              rowData.quantity = rowData.quantity || '';
                              rowData.total_amount = rowData.rate_per_unit * rowData.quantity || 0;
                              if (expenseError?.[`${index}_total_amount`]) expenseError[`${index}_total_amount`] = '';
                            }

                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);
                            setExpenseError({ ...expenseError });
                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                          placeholder="Select"
                        />
                      </>
                    );
                  }}
                />

                {/* Fee (hourly only) */}
                <Column
                  header="Fee"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    const feeType = getFeeType(rowEntry);
                    return (
                      <>
                        <span className="p-column-title text-break">Fee</span>
                        {feeType === 'fixed' ? (
                          <span className="text-muted">-</span>
                        ) : (
                          <>
                            <span className="text-break">
                              {rowEntry.rate_per_unit ? formatNumbersToCurrencyString(rowEntry.rate_per_unit) : <i>Please Enter</i>}
                            </span>
                            {/* <br></br> */}
                            <br />
                            {expenseError && expenseError[`${props.rowIndex}_rate_per_unit`] && (
                              <span className="text-danger">{expenseError[`${props.rowIndex}_rate_per_unit`]}</span>
                            )}
                          </>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    if (getFeeType(rowData) === 'fixed') return <span className="text-muted">-</span>;
                    return (
                      <>
                        <span className="p-column-title text-break">Fee</span>
                        <InputText
                          value={rowData?.rate_per_unit}
                          onChange={(e) => {
                            const index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            let quantity = rowData.quantity;
                            let rate = e.target.value;
                            quantity = quantity ? parseFloat(quantity) : 0;
                            rate = rate ? parseFloat(rate) : 0;
                            let total = quantity * rate;

                            rowData.rate_per_unit = fixToTwoDecimal(e.target.value);
                            rowData.total_amount = fixToTwoDecimal(total);
                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);

                            expenseError[`${props.rowIndex}_rate_per_unit`] = '';
                            setExpenseError({ ...expenseError });
                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                          className="w-100 input-shadow"
                          type="number"
                          min={0}
                          keyfilter={'pnum'}
                        />
                        {expenseError && expenseError[`${props.rowIndex}_rate_per_unit`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_rate_per_unit`]}</span>
                        )}
                      </>
                    );
                  }}
                />
                {/* Quantity (hourly only) */}
                <Column
                  header="Quantity"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    const feeType = getFeeType(rowEntry);
                    return (
                      <>
                        <span className="p-column-title text-break">Quantity</span>
                        {feeType === 'fixed' ? (
                          <span className="text-muted">-</span>
                        ) : (
                          <>
                            <span className="text-break">{rowEntry.quantity ? rowEntry.quantity : <i>Please Enter</i>}</span>
                            {/* <br></br> */}
                            <br />
                            {expenseError && expenseError[`${props.rowIndex}_quantity`] && (
                              <span className="text-danger">{expenseError[`${props.rowIndex}_quantity`]}</span>
                            )}
                          </>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    if (getFeeType(rowData) === 'fixed') return <span className="text-muted">-</span>;
                    return (
                      <>
                        <span className="p-column-title text-break">Quantity</span>
                        <InputText
                          value={rowData?.quantity}
                          onChange={(e) => {
                            const index = props.rowIndex;
                            const updatedRows = { ...editingRows };
                            const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                            let quantity = e.target.value;
                            let rate = rowData.rate_per_unit;
                            quantity = quantity ? parseFloat(quantity) : 0;
                            rate = rate ? parseFloat(rate) : 0;
                            let total = quantity * rate;

                            rowData.quantity = fixToTwoDecimal(e.target.value);
                            rowData.total_amount = fixToTwoDecimal(total);
                            updatedRows[index] = rowData;
                            setEditingRows(updatedRows);

                            expenseError[`${props.rowIndex}_quantity`] = '';
                            setExpenseError({ ...expenseError });
                            isChanged.expenses = true;
                            setIsChanged({ ...isChanged });
                            markRowAsModified(index);
                          }}
                          className="w-100 input-shadow"
                          min={0}
                          keyfilter={'pnum'}
                          type="number"
                          placeholder="Please Enter"
                        />
                        {expenseError && expenseError[`${props.rowIndex}_quantity`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_quantity`]}</span>
                        )}
                      </>
                    );
                  }}
                />
                <Column
                  header="Total"
                  body={(rowData, props) => {
                    const rowEntry = getRowData(props.rowIndex);
                    return (
                      <>
                        <span className="p-column-title text-break">Total</span>
                        <span className="text-break">{formatNumbersToCurrencyString(rowEntry?.total_amount)}</span>
                        <br />
                        {expenseError && expenseError[`${props.rowIndex}_total_amount`] && (
                          <span className="text-danger">{expenseError[`${props.rowIndex}_total_amount`]}</span>
                        )}
                      </>
                    );
                  }}
                  editor={(props) => {
                    const rowData = getRowData(props.rowIndex);
                    if (rowData?.fee_type === 'fixed') {
                      return (
                        <>
                          <span className="p-column-title text-break">Total</span>
                          <InputText
                            value={rowData?.total_amount}
                            onChange={(e) => {
                              const index = props.rowIndex;
                              const updatedRows = { ...editingRows };
                              const rowData = { ...(updatedRows[index] || initialExpenses[index]) };

                              rowData.total_amount = fixToTwoDecimal(e.target.value);
                              updatedRows[index] = rowData;
                              setEditingRows(updatedRows);
                              expenseError[`${props.rowIndex}_total_amount`] = '';
                              setExpenseError({ ...expenseError });
                              markRowAsModified(index);
                              isChanged.expenses = true;
                              setIsChanged({ ...isChanged });
                            }}
                            className="w-100 input-shadow"
                            type="number"
                            min={0}
                            placeholder="Please Enter"
                          />
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="p-column-title text-break">Total</span>
                        <span>{formatNumbersToCurrencyString(rowData?.total_amount)}</span>
                      </>
                    );
                  }}
                />
                <Column
                  header="Billable"
                  body={(rowData, props) => {
                    return billableExpenseBody({
                      rowData: getRowData(props.rowIndex),
                      props,
                      onChangeBillable,
                      disableExpense,
                    });
                  }}
                />
                <Column
                  className="time-entry-delete"
                  body={(rowData, props) => {
                    const rowIndex = props?.rowIndex;
                    return (
                      <>
                        <span className="p-column-title text-break">Actions</span>

                        {/* Save and Delete in one row */}
                        <div className="d-flex align-items-center gap-2">
                          {modifiedRows[rowIndex] && (
                            <>
                              <i
                                className={`bi bi-check2-circle cursor-pointer save-icon-row-${props?.rowIndex}`}
                                style={{
                                  marginRight: '10px',
                                  fontSize: '13px',
                                  marginTop: '1px',
                                }}
                                onClick={() => handleSaveRow(props?.rowIndex)}
                              ></i>
                              <Tooltip
                                target={`.save-icon-row-${rowIndex}`}
                                content="Save to add amount to total"
                                position="top"
                                showDelay={500}
                              />
                            </>
                          )}

                          <i
                            className="icon-delete icon-red cursor-pointer"
                            onClick={() => {
                              deleteExpense(props);
                              handleRowDeletion(rowIndex);
                            }}
                          />
                        </div>

                        {createExpenseInvoiceClicked && modifiedRows[rowIndex] && (
                          <span className="text-danger small mt-1">Please Save</span>
                        )}
                      </>
                    );
                  }}
                />
              </DataTable>
              <div>
                <div className="d-flex mt-2 p-2">
                  <div className="col col-md-8 text-bold">Total:</div>
                  <div className="col col-md-4 text-bold">{formatNumbersToCurrencyString(expenseTotal)}</div>
                </div>
                <div className="col-12 p-2">
                  <span className="mt-3 addfield" role="button" onClick={addExpense}>
                    + Add Expense
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Expenses;
